import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgPlanLimits } from '@/hooks/useOrgPlanLimits';
import { PLAN_NAME, getTeamAllowance } from '@/lib/plan-limits';
import { useAuth, StaffRole } from '@/contexts/AuthContext';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AdminPage, EmptyState, FilterChip, IconTile, PageHeader, Panel, PersonAvatar, SettingRow, StatCard, StatusChip,
  adminButton, adminTabs, useAdminPageTitle, type AdminTone,
} from '@/components/admin/ui';
import {
  Loader2, Plus, Pencil, Trash2, CalendarIcon, Clock, ArrowLeft, ChevronRight, ChevronLeft, Crown, Mail,
  Users, UserCheck, ShieldCheck, RefreshCw, Download, Upload, Copy, ArrowRight, Info,
} from 'lucide-react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { cn, renderDisplayText } from '@/lib/utils';

type TeamMemberRole = 'admin' | 'supervisor' | 'cleaner';

interface TeamMember {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  ical_import_url: string | null;
  ical_token: string | null;
  role: TeamMemberRole;
  date_of_birth: string | null;
  created_at: string;
}

// Common international dialing codes
const DIALING_CODES = [
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+353', country: 'Ireland', flag: '🇮🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+43', country: 'Austria', flag: '🇦🇹' },
  { code: '+48', country: 'Poland', flag: '🇵🇱' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰' },
  { code: '+358', country: 'Finland', flag: '🇫🇮' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
];

interface Availability {
  id: string;
  team_member_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface ScheduleOverride {
  id: string;
  team_member_id: string;
  override_date: string;
  start_time: string | null;
  end_time: string | null;
  is_blocked: boolean;
  reason: string | null;
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_AVAILABILITY: Omit<Availability, 'id' | 'team_member_id'>[] = [
  { day_of_week: 1, start_time: '09:00', end_time: '17:00', is_available: true },
  { day_of_week: 2, start_time: '09:00', end_time: '17:00', is_available: true },
  { day_of_week: 3, start_time: '09:00', end_time: '17:00', is_available: true },
  { day_of_week: 4, start_time: '09:00', end_time: '17:00', is_available: true },
  { day_of_week: 5, start_time: '09:00', end_time: '17:00', is_available: true },
];

const ROLE_META: Record<TeamMemberRole, { label: string; tone: AdminTone }> = {
  admin: { label: 'Admin', tone: 'violet' },
  supervisor: { label: 'Supervisor', tone: 'blue' },
  cleaner: { label: 'Cleaner', tone: 'mint' },
};

const DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE = 'A team member with this email already exists.';

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'Something went wrong';
};

// supabase.functions.invoke hides the function's JSON error behind a generic
// "non-2xx" message; read the real one (e.g. a plan limit) when it's there.
const getInviteErrorMessage = async (error: unknown) => {
  const context = (error as { context?: { json?: () => Promise<unknown> } } | null)?.context;
  if (context && typeof context.json === 'function') {
    try {
      const body = (await context.json()) as { error?: unknown } | null;
      if (body && typeof body.error === 'string' && body.error.trim()) return body.error;
    } catch {
      // fall through to the generic message
    }
  }
  return getErrorMessage(error);
};

const BILLING_PATH = '/admin/billing';

const isDuplicateTeamMemberEmailError = (error: unknown) => {
  const supabaseError = error as { code?: string; message?: string; details?: string } | null;
  const text = `${supabaseError?.message ?? ''} ${supabaseError?.details ?? ''}`;

  return supabaseError?.code === '23505' && text.includes('team_members_org_email_key');
};

const TeamAdmin = () => {
  useAdminPageTitle('My Team');
  const { toast } = useToast();
  const { user, isPlatformAdmin, isOrgOwner, refreshOrgMembership } = useAuth();
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [overrides, setOverrides] = useState<ScheduleOverride[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', dialingCode: '+44', phone: '', role: 'cleaner' as TeamMemberRole, dateOfBirth: null as Date | null });
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [newOverride, setNewOverride] = useState({ start_time: '', end_time: '', is_blocked: true, reason: 'Off' });
  const [adminProfile, setAdminProfile] = useState<{ name: string | null; avatar_url: string | null } | null>(null);
  const [currentWeek, setCurrentWeek] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferUserId, setTransferUserId] = useState('');
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);

  const canManageTeam = canWrite && (isPlatformAdmin || isOrgOwner);
  const queryClient = useQueryClient();
  const { data: planLimits } = useOrgPlanLimits(orgId);
  // Plan limits (null while loading or if they can't be read: then the page
  // behaves as before and the database is the backstop).
  const allowance = planLimits ? getTeamAllowance(planLimits, teamMembers.length) : null;
  const canAddMember = !allowance || allowance.canAdd;
  const staffLoginsAllowed = !planLimits || planLimits.staffLogins;
  const isOwnEmail = (email: string) => Boolean(user?.email) && normalizeEmail(email) === normalizeEmail(user!.email!);
  // An invite creates a staff login: not on Solo, and not while over the limit.
  // Your own seat is fine (the Solo owner who is also the cleaner).
  const canInvite = (email: string) => isOwnEmail(email) || (staffLoginsAllowed && !allowance?.overLimit);
  const upgradePlanName = allowance?.upgradeTo ? PLAN_NAME[allowance.upgradeTo] : null;
  const refreshPlanLimits = () => queryClient.invalidateQueries({ queryKey: ['org-plan-limits', orgId] });
  const canTransferOwnership = canWrite && isOrgOwner;
  const canEditSelectedSchedule = canWrite && Boolean(selectedMember) && (canManageTeam || selectedMember?.user_id === user?.id);

  const BLOCK_REASONS = ['Off', 'Sick', 'Holiday', 'Training', 'Other'] as const;

  useEffect(() => {
    if (orgId) fetchTeamMembers();
    fetchAdminProfile();
  // Load team/admin data when the active org changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (selectedMember) {
      fetchMemberSchedule(selectedMember.id);
    }
  // Reload the schedule when the selected member changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember]);

  const sortTeamMembers = (members: TeamMember[]) => [...members].sort((a, b) => {
    if (a.user_id === user?.id) return -1;
    if (b.user_id === user?.id) return 1;
    return a.name.localeCompare(b.name);
  });

  const fetchAdminProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('name, avatar_url')
      .eq('id', user.id)
      .single();
    if (data) {
      setAdminProfile(data);
    }
  };

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('organization_id', orgId)
        .order('name');

      if (error) throw error;
      
      const sortedData = data ? sortTeamMembers(data.map(member => ({
        ...member,
        role: (member.role as TeamMemberRole) || 'cleaner',
      }))) : [];
      
      setTeamMembers(sortedData);
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberSchedule = async (memberId: string) => {
    try {
      const [availabilityRes, overridesRes] = await Promise.all([
        supabase.from('team_availability').select('*').eq('team_member_id', memberId),
        supabase.from('team_schedule_overrides').select('*').eq('team_member_id', memberId).order('override_date'),
      ]);

      if (availabilityRes.error) throw availabilityRes.error;
      if (overridesRes.error) throw overridesRes.error;

      setAvailability(availabilityRes.data || []);
      setOverrides(overridesRes.data || []);
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  // Single source of truth for triggering a fresh invitation email. The edge
  // function always generates a new OTP and sends a new email, so this is safe
  // to call both when adding a member and when resending to an existing one.
  // Only the team member id is sent: the function derives the invitee's email,
  // name and role from the locked team_members row (never from request metadata).
  const sendTeamInvite = (member: { id: string }) =>
    supabase.functions.invoke('send-team-invite', {
      body: { teamMemberId: member.id },
    });

  const handleResendInvite = async (member: TeamMember) => {
    if (!canManageTeam) return;
    if (!canInvite(member.email)) {
      toast({
        title: 'Invites aren’t included',
        description: staffLoginsAllowed
          ? allowance?.notice ?? 'Your plan has no room for more staff logins.'
          : 'Your Solo plan doesn’t include staff logins. Upgrade to Team to invite your staff.',
        variant: 'destructive',
      });
      return;
    }
    setResendingInviteId(member.id);
    try {
      const { error } = await sendTeamInvite(member);
      if (error) throw new Error(await getInviteErrorMessage(error));
      toast({
        title: 'Invitation sent',
        description: `A fresh verification email was sent to ${member.email}.`,
      });
    } catch (error: unknown) {
      toast({
        title: 'Could not send invitation',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleAddMember = async () => {
    if (!canManageTeam) return;

    const name = newMember.name.trim();
    const email = normalizeEmail(newMember.email);

    if (!name || !email) {
      toast({
        title: 'Error',
        description: 'Name and email are required',
        variant: 'destructive',
      });
      return;
    }

    if (!canAddMember) {
      toast({
        title: 'Team is full',
        description: allowance?.notice ?? 'Your plan has no room for more team members.',
        variant: 'destructive',
      });
      return;
    }

    if (teamMembers.some((member) => normalizeEmail(member.email) === email)) {
      toast({
        title: 'Error',
        description: DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE,
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      // Combine dialing code and phone number
      const phone = newMember.phone.trim();
      const fullPhone = phone ? `${newMember.dialingCode} ${phone}` : null;
      
      const { data: memberData, error: memberError } = await supabase
        .from('team_members')
        .insert({
          organization_id: orgId,
          name,
          email,
          phone: fullPhone,
          role: newMember.role,
          date_of_birth: newMember.dateOfBirth ? format(newMember.dateOfBirth, 'yyyy-MM-dd') : null,
        })
        .select()
        .single();

      if (memberError) throw memberError;

      // Add default availability
      const availabilityInserts = DEFAULT_AVAILABILITY.map((a) => ({
        team_member_id: memberData.id,
        day_of_week: a.day_of_week,
        start_time: a.start_time,
        end_time: a.end_time,
        is_available: a.is_available,
      }));

      const { error: availError } = await supabase.from('team_availability').insert(availabilityInserts);
      if (availError) throw availError;

      // Send the invitation email, unless the plan doesn't include staff logins.
      const inviteAllowed = canInvite(email);
      const { error: inviteError } = inviteAllowed
        ? await sendTeamInvite({ id: memberData.id })
        : { error: null };

      if (!inviteAllowed) {
        toast({
          title: 'Member added',
          description: 'Your Solo plan doesn’t include staff logins, so no invite was sent. Upgrade to Team to give staff their own login.',
        });
      } else if (inviteError) {
        console.error('Error sending invite:', inviteError);
        toast({
          title: 'Member added',
          description: 'Team member was added but the invitation email could not be sent. You can resend it later.',
          variant: 'default',
        });
      } else {
        toast({ 
          title: 'Success', 
          description: 'Team member added and invitation email sent!' 
        });
      }

      const addedMember: TeamMember = {
        id: memberData.id,
        user_id: memberData.user_id,
        name: memberData.name,
        email: memberData.email,
        phone: memberData.phone,
        ical_import_url: memberData.ical_import_url,
        ical_token: memberData.ical_token,
        role: (memberData.role as TeamMemberRole) || newMember.role,
        date_of_birth: memberData.date_of_birth,
        created_at: memberData.created_at,
      };
      setTeamMembers((current) => sortTeamMembers([
        ...current.filter((member) => member.id !== addedMember.id),
        addedMember,
      ]));
      setIsAddDialogOpen(false);
      setNewMember({ name: '', email: '', dialingCode: '+44', phone: '', role: 'cleaner', dateOfBirth: null });
      refreshPlanLimits();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: isDuplicateTeamMemberEmailError(error) ? DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE : getErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!canManageTeam) return;
    if (!editMember) return;

    const name = editMember.name.trim();
    const email = normalizeEmail(editMember.email);

    if (!name || !email) {
      toast({
        title: 'Error',
        description: 'Name and email are required',
        variant: 'destructive',
      });
      return;
    }

    if (teamMembers.some((member) => member.id !== editMember.id && normalizeEmail(member.email) === email)) {
      toast({
        title: 'Error',
        description: DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE,
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const updatedMember = { ...editMember, name, email };
      const { error } = await supabase
        .from('team_members')
        .update({
          name,
          email,
          phone: editMember.phone,
          ical_import_url: editMember.ical_import_url,
          role: editMember.role,
        })
        .eq('id', editMember.id)
        .eq('organization_id', orgId);

      if (error) throw error;

      toast({ title: 'Success', description: 'Team member updated' });
      setIsEditDialogOpen(false);
      setTeamMembers((current) => sortTeamMembers(current.map((member) => (
        member.id === updatedMember.id ? updatedMember : member
      ))));
      if (selectedMember?.id === editMember.id) {
        setSelectedMember(updatedMember);
      }
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: isDuplicateTeamMemberEmailError(error) ? DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE : getErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (id: string) => {
    if (!canManageTeam) return;
    if (!confirm('Are you sure you want to delete this team member?')) return;

    try {
      const { error } = await supabase.from('team_members').delete().eq('id', id).eq('organization_id', orgId);
      if (error) throw error;

      toast({ title: 'Success', description: 'Team member deleted' });
      refreshPlanLimits();
      if (selectedMember?.id === id) {
        setSelectedMember(null);
      }
      fetchTeamMembers();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const handleUpdateAvailability = async (dayOfWeek: number, field: string, value: string | boolean) => {
    if (!canEditSelectedSchedule) return;
    if (!selectedMember) return;

    const existing = availability.find((a) => a.day_of_week === dayOfWeek);

    try {
      if (existing) {
        const { error } = await supabase
          .from('team_availability')
          .update({ [field]: value })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('team_availability').insert({
          team_member_id: selectedMember.id,
          day_of_week: dayOfWeek,
          start_time: field === 'start_time' ? value : '09:00',
          end_time: field === 'end_time' ? value : '17:00',
          is_available: field === 'is_available' ? value : true,
        });
        if (error) throw error;
      }

      fetchMemberSchedule(selectedMember.id);
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const handleAddOverride = async () => {
    if (!canEditSelectedSchedule) return;
    if (!selectedMember || !selectedDate) return;

    setSaving(true);
    try {
      // The table is scoped through team_member_id (no organization_id column).
      const { error } = await supabase.from('team_schedule_overrides').upsert({
        team_member_id: selectedMember.id,
        override_date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: newOverride.is_blocked ? null : newOverride.start_time || null,
        end_time: newOverride.is_blocked ? null : newOverride.end_time || null,
        is_blocked: newOverride.is_blocked,
        reason: newOverride.reason || null,
      }, {
        onConflict: 'team_member_id,override_date',
      });

      if (error) throw error;

      toast({ title: 'Success', description: 'Schedule override added' });
      setIsScheduleDialogOpen(false);
      setSelectedDate(undefined);
      setNewOverride({ start_time: '', end_time: '', is_blocked: false, reason: '' });
      fetchMemberSchedule(selectedMember.id);
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOverride = async (id: string) => {
    if (!canEditSelectedSchedule) return;
    try {
      const { error } = await supabase.from('team_schedule_overrides').delete().eq('id', id);
      if (error) throw error;

      toast({ title: 'Success', description: 'Override removed' });
      if (selectedMember) fetchMemberSchedule(selectedMember.id);
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const handleTransferOwnership = async () => {
    if (!canTransferOwnership || !transferUserId) return;
    setSaving(true);
    try {
      const { error } = await supabase.rpc('transfer_org_ownership' as never, {
        _org_id: orgId,
        _new_owner_user_id: transferUserId,
      });
      if (error) throw error;
      toast({ title: 'Ownership transferred', description: 'The selected team member is now the business owner.' });
      setTransferDialogOpen(false);
      setTransferUserId('');
      await refreshOrgMembership();
      fetchTeamMembers();
    } catch (error: unknown) {
      toast({ title: 'Transfer failed', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hours = Math.floor(i / 2);
    const mins = i % 2 === 0 ? '00' : '30';
    const time = `${hours.toString().padStart(2, '0')}:${mins}`;
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return { time, label: `${displayHours}:${mins} ${ampm}` };
  });

  const memberAvatarSrc = (member: TeamMember) =>
    member.user_id && member.user_id === user?.id ? adminProfile?.avatar_url ?? null : null;

  const roleCounts = teamMembers.reduce(
    (acc, member) => {
      acc[member.role] = (acc[member.role] ?? 0) + 1;
      return acc;
    },
    {} as Record<TeamMemberRole, number>,
  );
  const joinedCount = teamMembers.filter((member) => member.user_id).length;
  const pendingInviteCount = teamMembers.length - joinedCount;
  const leadCount = (roleCounts.admin ?? 0) + (roleCounts.supervisor ?? 0);

  const renderRoleChip = (role: TeamMemberRole, className?: string) => {
    const meta = ROLE_META[role] ?? { label: String(role), tone: 'grey' as AdminTone };
    return (
      <StatusChip tone={meta.tone} dot={false} className={cn('capitalize', className)}>
        {meta.label}
      </StatusChip>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const addMemberBlocked = canManageTeam && !canAddMember && (
    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
      <Button size="sm" className={cn(adminButton.primary, 'w-full sm:w-auto')} disabled>
        <Plus className="h-4 w-4" />
        Add Team Member
      </Button>
      {upgradePlanName && isOrgOwner && (
        <Button asChild size="sm" variant="outline" className={cn(adminButton.outline, 'w-full sm:w-auto')}>
          <Link to={BILLING_PATH}>
            Upgrade to {upgradePlanName}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      )}
    </div>
  );

  const addMemberDialog = canManageTeam && canAddMember && (
    <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className={cn(adminButton.primary, 'w-full sm:w-auto')}>
          <Plus className="h-4 w-4" />
          Add Team Member
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogDescription>Add a new member to your team</DialogDescription>
        </DialogHeader>
        {!staffLoginsAllowed && (
          <p className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3 text-[12.5px] leading-snug text-muted-foreground" data-testid="solo-no-staff-logins">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>
              Solo doesn’t include staff logins, so we won’t send an invite unless this is you.{' '}
              <Link to={BILLING_PATH} className="font-semibold text-primary hover:underline">Upgrade to Team</Link>{' '}
              to give staff their own login.
            </span>
          </p>
        )}
        <div className="adm-form space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={newMember.name}
              onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              placeholder="Full name"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              type="email"
              value={newMember.email}
              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              placeholder="email@example.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Phone (optional)</Label>
            <div className="flex gap-2">
              <Select
                value={newMember.dialingCode}
                onValueChange={(value) => setNewMember({ ...newMember, dialingCode: value })}
              >
                <SelectTrigger className="w-[112px] shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIALING_CODES.map((dc) => (
                    <SelectItem key={dc.code} value={dc.code}>
                      {dc.flag} {dc.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={newMember.phone}
                onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                placeholder="Phone number"
                className="flex-1"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Date of Birth (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    adminButton.outline,
                    'w-full justify-start text-left font-normal',
                    !newMember.dateOfBirth && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="h-4 w-4" />
                  {newMember.dateOfBirth ? format(newMember.dateOfBirth, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={newMember.dateOfBirth || undefined}
                  onSelect={(date) => setNewMember({ ...newMember, dateOfBirth: date || null })}
                  disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select
              value={newMember.role}
              onValueChange={(value: TeamMemberRole) => setNewMember({ ...newMember, role: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="cleaner">Cleaner</SelectItem>
              </SelectContent>
            </Select>
            <p className="adm-hint">
              {newMember.role === 'admin' && 'Full access to all features and website editing'}
              {newMember.role === 'supervisor' && 'Can view all calendars and manage enquiries, no website editing'}
              {newMember.role === 'cleaner' && 'Can only view and manage their own calendar'}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setIsAddDialogOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" className={adminButton.primary} onClick={handleAddMember} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Add Member
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <AdminPage>
      {!selectedMember && (
        <PageHeader
          description="Manage team members and their schedules"
          actions={addMemberDialog || addMemberBlocked || undefined}
        />
      )}

      {!selectedMember && allowance?.notice && (
        <div
          role="status"
          data-testid="team-plan-limit-notice"
          className={cn(
            'flex flex-col gap-2 rounded-xl border p-4 text-[13px] sm:flex-row sm:items-center sm:justify-between',
            allowance.overLimit
              ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100'
              : 'border-border bg-secondary/60 text-secondary-foreground',
          )}
        >
          <p className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {allowance.notice}
              {allowance.overLimit && ' Everyone already on the team keeps working.'}
            </span>
          </p>
          {upgradePlanName && isOrgOwner && (
            <Link to={BILLING_PATH} className="inline-flex shrink-0 items-center gap-1 font-semibold text-primary hover:underline">
              Upgrade to {upgradePlanName} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
        </div>
      )}

      {!selectedMember && teamMembers.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
          <StatCard
            label="Team members"
            icon={<Users />}
            value={teamMembers.length}
            hint={`${roleCounts.cleaner ?? 0} ${(roleCounts.cleaner ?? 0) === 1 ? 'cleaner' : 'cleaners'}`}
          />
          <StatCard label="Joined" icon={<UserCheck />} value={joinedCount} hint="Signed in to their account" />
          <StatCard label="Pending invites" icon={<Mail />} value={pendingInviteCount} hint="Awaiting sign-up" />
          <StatCard label="Managers" icon={<ShieldCheck />} value={leadCount} hint="Admins & supervisors" />
        </div>
      )}

      {canTransferOwnership && !selectedMember && (
        <Panel padded={false} bodyClassName="px-4 md:px-5">
          <SettingRow
            icon={<Crown />}
            iconTone="amber"
            title="Ownership"
            description="Transfer the business owner role to an existing signed-in member."
            control={
              <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className={adminButton.outline}>Transfer ownership</Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[460px]">
                  <DialogHeader>
                    <DialogTitle>Transfer ownership</DialogTitle>
                    <DialogDescription>The selected member becomes owner and current owners become members.</DialogDescription>
                  </DialogHeader>
                  <div className="adm-form space-y-1.5 py-2">
                    <Label>New owner</Label>
                    <Select value={transferUserId} onValueChange={setTransferUserId}>
                      <SelectTrigger><SelectValue placeholder="Select a member" /></SelectTrigger>
                      <SelectContent>
                        {teamMembers.filter((member) => member.user_id && member.user_id !== user?.id).map((member) => (
                          <SelectItem key={member.id} value={member.user_id!}>{member.name} — {member.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
                    <Button size="sm" className={adminButton.primary} onClick={handleTransferOwnership} disabled={!transferUserId || saving}>
                      {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                      Confirm transfer
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            }
          />
        </Panel>
      )}

      {/* Show team list when no member selected, show calendar when member selected */}
      {!selectedMember ? (
        <Panel
          title="Team Members"
          description={allowance ? allowance.usageLabel : `${teamMembers.length} members`}
          padded={false}
          bodyClassName="pt-3"
        >
          {teamMembers.length > 0 && (
            <div
              className="hidden grid-cols-[minmax(0,2.4fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_112px] items-center gap-4 border-y border-border bg-muted/55 px-5 py-2.5 text-[11.5px] font-semibold tracking-[0.02em] text-muted-foreground md:grid"
              aria-hidden="true"
            >
              <span>Member</span>
              <span>Role</span>
              <span>Phone</span>
              <span>Status</span>
              <span className="text-right">Actions</span>
            </div>
          )}
          <div className="adm-divide border-t border-border md:border-t-0">
            {teamMembers.map((member) => {
              const isCurrentUser = member.user_id === user?.id;
              return (
                <div
                  key={member.id}
                  role="button"
                  tabIndex={0}
                  className="grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 transition-colors hover:bg-[hsl(var(--adm-hover)/0.7)] focus-visible:bg-[hsl(var(--adm-hover))] focus-visible:outline-none md:grid-cols-[minmax(0,2.4fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_112px] md:gap-4 md:px-5"
                  onClick={() => setSelectedMember(member)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedMember(member);
                    }
                  }}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <PersonAvatar name={member.name} seed={member.id} src={memberAvatarSrc(member)} size={36} />
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-[13.5px] font-semibold text-foreground">
                          {member.name}
                          {isCurrentUser && <span className="ml-1 font-normal text-muted-foreground">(me)</span>}
                        </p>
                        {renderRoleChip(member.role, 'md:hidden')}
                      </div>
                      <p className="truncate text-[12.5px] text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="hidden md:block">{renderRoleChip(member.role)}</div>
                  <p className="adm-num hidden truncate text-[13px] text-foreground/80 md:block">
                    {member.phone || <span className="text-muted-foreground">—</span>}
                  </p>
                  <div className="hidden md:block">
                    {member.user_id ? (
                      <StatusChip tone="green">Active</StatusChip>
                    ) : canInvite(member.email) ? (
                      <StatusChip tone="amber">Invite pending</StatusChip>
                    ) : (
                      <StatusChip tone="grey">No login</StatusChip>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center justify-end gap-0.5">
                    {canManageTeam && (
                      <>
                        {!member.user_id && canInvite(member.email) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn(adminButton.iconGhost, 'hidden sm:inline-flex')}
                            title="Resend invitation email"
                            aria-label="Resend invitation email"
                            disabled={resendingInviteId === member.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendInvite(member);
                            }}
                          >
                            {resendingInviteId === member.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Mail className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(adminButton.iconGhost, 'hidden sm:inline-flex')}
                          title="Update member details"
                          aria-label={`Update details for ${member.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditMember(member);
                            setIsEditDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(adminButton.iconGhost, 'hidden hover:bg-destructive/10 hover:text-destructive sm:inline-flex')}
                          title="Remove team member"
                          aria-label={`Remove ${member.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMember(member.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <ChevronRight className="ml-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  </div>
                </div>
              );
            })}
          </div>
          {teamMembers.length === 0 && (
            <EmptyState
              icon={<Users />}
              title="No team members yet"
              description="Add your cleaners and supervisors to assign jobs and manage their availability."
            />
          )}
        </Panel>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            className={cn(adminButton.ghost, '-ml-2')}
            onClick={() => setSelectedMember(null)}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to team
          </Button>

          <Panel padded={false}>
            <div className="flex flex-col gap-4 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between md:px-5">
              <div className="flex min-w-0 items-center gap-3">
                <PersonAvatar
                  name={selectedMember.name}
                  seed={selectedMember.id}
                  src={memberAvatarSrc(selectedMember)}
                  size={44}
                />
                <div className="min-w-0">
                  <h2 className="truncate text-[16px] font-semibold leading-tight text-foreground">
                    {selectedMember.name}{renderDisplayText("'s")} Schedule
                  </h2>
                  <p className="mt-0.5 truncate text-[12.5px] text-muted-foreground">{selectedMember.email}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {renderRoleChip(selectedMember.role)}
                {selectedMember.user_id ? (
                  <StatusChip tone="green">Active</StatusChip>
                ) : canInvite(selectedMember.email) ? (
                  <StatusChip tone="amber">Invite pending</StatusChip>
                ) : (
                  <StatusChip tone="grey">No login</StatusChip>
                )}
              </div>
            </div>

            <div className="p-4 md:p-5">
              <Tabs defaultValue="calendar" className="w-full overflow-hidden">
                <TabsList className={cn(adminTabs.list, 'mb-4 max-w-full overflow-x-auto')}>
                  <TabsTrigger value="calendar" className={adminTabs.trigger}>
                    <CalendarIcon className="hidden sm:block" />
                    Calendar
                  </TabsTrigger>
                  <TabsTrigger value="work-schedule" className={adminTabs.trigger}>
                    <Clock className="hidden sm:block" />
                    Work Schedule
                  </TabsTrigger>
                  <TabsTrigger value="ical" className={adminTabs.trigger}>
                    <RefreshCw className="hidden sm:block" />
                    Calendar Sync
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="calendar" className="mt-0 space-y-3">
                  {/* Week Navigation */}
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className={adminButton.icon}
                        aria-label="Previous week"
                        onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
                      >
                        <ChevronLeft />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(adminButton.outline, 'h-8')}
                        onClick={() => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                      >
                        Today
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={adminButton.icon}
                        aria-label="Next week"
                        onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                      >
                        <ChevronRight />
                      </Button>
                    </div>
                    <p className="ml-1 text-[14px] font-semibold text-foreground">
                      {format(currentWeek, 'd MMM')} – {format(addDays(currentWeek, 6), 'd MMM yyyy')}
                    </p>
                    <div className="ml-auto hidden items-center gap-3 text-[11.5px] text-muted-foreground sm:flex">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-[3px] border border-primary/25 bg-primary/20" />
                        Working hours
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-[3px] border border-destructive/25 bg-destructive/15" />
                        Blocked
                      </span>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-border">
                    <div className="min-w-[600px]">
                      {/* Day headers */}
                      <div className="flex border-b border-border bg-muted/40">
                        <div className="w-12 flex-shrink-0 sm:w-16" /> {/* Time column spacer */}
                        {Array.from({ length: 7 }, (_, i) => {
                          const day = addDays(currentWeek, i);
                          const isToday = isSameDay(day, new Date());
                          const override = overrides.find((o) => o.override_date === format(day, 'yyyy-MM-dd'));

                          return (
                            <div
                              key={i}
                              className="flex min-w-[70px] flex-1 flex-col items-center border-l border-border py-2"
                            >
                              <p className={cn('text-[10.5px] font-semibold uppercase tracking-wide', isToday ? 'text-primary' : 'text-muted-foreground')}>
                                {format(day, 'EEE')}
                              </p>
                              <p
                                className={cn(
                                  'adm-num mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-[14px] font-semibold',
                                  isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
                                )}
                              >
                                {format(day, 'd')}
                              </p>
                              {override?.is_blocked && (
                                <span
                                  className={cn(
                                    'adm-chip mt-1',
                                    override.reason === 'Sick' && 'adm-chip--amber',
                                    override.reason === 'Holiday' && 'adm-chip--blue',
                                    override.reason === 'Training' && 'adm-chip--violet',
                                    (!override.reason || override.reason === 'Off' || override.reason === 'Other') && 'adm-chip--rose'
                                  )}
                                >
                                  {override.reason || 'Off'}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Time grid */}
                      <div
                        className="adm-scroll h-[400px] overflow-y-auto"
                        ref={(el) => {
                          // Scroll to 07:00 on mount (4 slots from 05:00 start = 4 * 24px)
                          if (el && el.scrollTop === 0) {
                            el.scrollTop = 96;
                          }
                        }}
                      >
                        <div className="relative">
                          {/* Time slots from 05:00 to 22:00 in 30-min increments */}
                          {Array.from({ length: 34 }, (_, slotIndex) => {
                            const hour = Math.floor(slotIndex / 2) + 5; // Start from 05:00
                            const minutes = (slotIndex % 2) * 30;
                            const timeLabel = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                            const isHourStart = minutes === 0;

                            return (
                              <div
                                key={slotIndex}
                                className={cn('flex h-6', isHourStart ? 'border-b border-dashed border-border/60' : 'border-b border-border/80')}
                              >
                                {/* Time label - only show on hour */}
                                <div className="adm-num relative w-12 flex-shrink-0 border-r border-border pr-1.5 text-right text-[10.5px] text-muted-foreground sm:w-16 sm:pr-2">
                                  {isHourStart && <span className="relative top-[5px]">{timeLabel}</span>}
                                </div>

                                {/* Day columns */}
                                {Array.from({ length: 7 }, (_, dayIndex) => {
                                  const day = addDays(currentWeek, dayIndex);
                                  const dayOfWeek = day.getDay();
                                  const dayAvail = availability.find((a) => a.day_of_week === dayOfWeek);
                                  const override = overrides.find((o) => o.override_date === format(day, 'yyyy-MM-dd'));
                                  const isToday = isSameDay(day, new Date());
                                  const isBlocked = override?.is_blocked;

                                  // Check if this time slot is within working hours
                                  let isWorkingSlot = false;
                                  if (dayAvail?.is_available && !isBlocked) {
                                    const startHour = parseInt(dayAvail.start_time?.slice(0, 2) || '9');
                                    const startMin = parseInt(dayAvail.start_time?.slice(3, 5) || '0');
                                    const endHour = parseInt(dayAvail.end_time?.slice(0, 2) || '17');
                                    const endMin = parseInt(dayAvail.end_time?.slice(3, 5) || '0');
                                    const slotTime = hour * 60 + minutes;
                                    const startTime = startHour * 60 + startMin;
                                    const endTime = endHour * 60 + endMin;
                                    isWorkingSlot = slotTime >= startTime && slotTime < endTime;
                                  }

                                  return (
                                    <div
                                      key={dayIndex}
                                      className={cn(
                                        'min-w-[70px] flex-1 cursor-pointer border-l border-border transition-colors hover:bg-[hsl(var(--adm-hover))]',
                                        isToday && 'bg-primary/[0.03]',
                                        isBlocked && 'adm-stripes bg-destructive/[0.07] hover:bg-destructive/10',
                                        isWorkingSlot && !isBlocked && 'bg-primary/[0.16] hover:bg-primary/[0.22]'
                                      )}
                                      onClick={() => {
                                        setSelectedDate(day);
                                        if (override) {
                                          setNewOverride({
                                            start_time: override.start_time?.slice(0, 5) || '',
                                            end_time: override.end_time?.slice(0, 5) || '',
                                            is_blocked: override.is_blocked,
                                            reason: override.reason || 'Off',
                                          });
                                        } else {
                                          setNewOverride({ start_time: '', end_time: '', is_blocked: true, reason: 'Off' });
                                        }
                                        setIsScheduleDialogOpen(true);
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-[12px] text-muted-foreground">Click any day to mark it as off, sick, holiday or training.</p>

                  {/* Block Date Dialog */}
                  <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
                    <DialogContent className="sm:max-w-[460px]">
                      <DialogHeader>
                        <DialogTitle>
                          {selectedDate ? format(selectedDate, 'EEEE, d MMMM yyyy') : 'Block Date'}
                        </DialogTitle>
                        <DialogDescription>
                          Mark this day as unavailable
                        </DialogDescription>
                      </DialogHeader>
                      <div className="adm-form space-y-4 py-2">
                        <div className="space-y-2">
                          <Label>Reason</Label>
                          <div className="flex flex-wrap gap-1.5">
                            {BLOCK_REASONS.map((reason) => (
                              <FilterChip
                                key={reason}
                                active={newOverride.reason === reason}
                                onClick={() => setNewOverride({ ...newOverride, reason, is_blocked: true })}
                              >
                                {reason}
                              </FilterChip>
                            ))}
                          </div>
                        </div>
                        {newOverride.reason === 'Other' && (
                          <div className="space-y-1.5">
                            <Label>Custom reason</Label>
                            <Input
                              value={newOverride.reason === 'Other' ? '' : newOverride.reason}
                              onChange={(e) =>
                                setNewOverride({ ...newOverride, reason: e.target.value || 'Other' })
                              }
                              placeholder="Enter reason..."
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col-reverse justify-between gap-2 pt-2 sm:flex-row">
                        <Button
                          variant="outline"
                          size="sm"
                          className={adminButton.danger}
                          onClick={() => {
                            if (selectedDate) {
                              const existingOverride = overrides.find(
                                (o) => o.override_date === format(selectedDate, 'yyyy-MM-dd')
                              );
                              if (existingOverride) {
                                handleDeleteOverride(existingOverride.id);
                              }
                            }
                            setIsScheduleDialogOpen(false);
                          }}
                        >
                          Clear Block
                        </Button>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setIsScheduleDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button size="sm" className={adminButton.primary} onClick={handleAddOverride} disabled={saving || !selectedDate}>
                            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Block Day
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </TabsContent>

                <TabsContent value="work-schedule" className="mt-0 space-y-3">
                  <p className="text-[13px] text-muted-foreground">
                    Set the regular weekly working hours for this team member
                  </p>
                  <div className="adm-divide overflow-hidden rounded-xl border border-border">
                    {DAYS_OF_WEEK.map((day, index) => {
                      const dayAvail = availability.find((a) => a.day_of_week === index);
                      const isWorking = dayAvail?.is_available ?? false;
                      return (
                        <div
                          key={day}
                          className="flex flex-col gap-3 px-4 py-3 sm:min-h-[60px] sm:flex-row sm:items-center sm:gap-4"
                        >
                          <div className="flex items-center justify-between gap-3 sm:w-[230px] sm:shrink-0 sm:justify-start">
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={isWorking}
                                aria-label={`${day} working`}
                                onCheckedChange={(checked) =>
                                  handleUpdateAvailability(index, 'is_available', checked)
                                }
                              />
                              <span className="w-[86px] text-[13.5px] font-semibold text-foreground">{day}</span>
                            </div>
                            <StatusChip tone={isWorking ? 'green' : 'grey'}>{isWorking ? 'Working' : 'Off'}</StatusChip>
                          </div>
                          {isWorking ? (
                            <div className="adm-form flex items-center gap-2">
                              <Label className="sr-only">Start</Label>
                              <Select
                                value={dayAvail?.start_time?.slice(0, 5) || '09:00'}
                                onValueChange={(value) => handleUpdateAvailability(index, 'start_time', value)}
                              >
                                <SelectTrigger className="w-full sm:w-[132px]" aria-label={`${day} start`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {timeOptions.map((opt) => (
                                    <SelectItem key={opt.time} value={opt.time}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span className="text-[12px] text-muted-foreground">to</span>
                              <Label className="sr-only">End</Label>
                              <Select
                                value={dayAvail?.end_time?.slice(0, 5) || '17:00'}
                                onValueChange={(value) => handleUpdateAvailability(index, 'end_time', value)}
                              >
                                <SelectTrigger className="w-full sm:w-[132px]" aria-label={`${day} end`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {timeOptions.map((opt) => (
                                    <SelectItem key={opt.time} value={opt.time}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <p className="hidden text-[12.5px] text-muted-foreground sm:block">Not working</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="ical" className="mt-0">
                  <div className="adm-form adm-divide rounded-xl border border-border">
                    <div className="flex items-start gap-3 p-4">
                      <IconTile tone="blue"><Download /></IconTile>
                      <div className="min-w-0 flex-1 space-y-2">
                        <div>
                          <Label>Import from External Calendar</Label>
                          <p className="adm-hint mt-0.5">
                            Paste an iCal URL to import busy times from an external calendar (Google, Outlook, etc.)
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <Input
                            value={selectedMember.ical_import_url || ''}
                            disabled={!canWrite}
                            onChange={(e) => {
                              if (selectedMember) {
                                setSelectedMember({ ...selectedMember, ical_import_url: e.target.value });
                              }
                            }}
                            placeholder="https://calendar.google.com/..."
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!assertCanWrite()) return;
                              if (!selectedMember) return;
                              setSaving(true);
                              try {
                                const { error } = await supabase
                                  .from('team_members')
                                  .update({ ical_import_url: selectedMember.ical_import_url })
                                  .eq('id', selectedMember.id);
                                if (error) throw error;
                                toast({ title: 'Success', description: 'iCal URL saved' });
                              } catch (error: unknown) {
                                toast({ title: 'Error', description: getErrorMessage(error), variant: 'destructive' });
                              } finally {
                                setSaving(false);
                              }
                            }}
                            disabled={!canWrite || saving}
                            className={cn(adminButton.outline, 'w-full shrink-0 sm:w-auto')}
                          >
                            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Save iCal URL
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4">
                      <IconTile tone="violet"><Upload /></IconTile>
                      <div className="min-w-0 flex-1 space-y-2">
                        <div>
                          <Label>Export Availability</Label>
                          <p className="adm-hint mt-0.5">
                            Subscribe to this team member's availability in your calendar app
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <Input
                            readOnly
                            value={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ical-export?memberId=${selectedMember.id}&token=${selectedMember.ical_token ?? ''}`}
                            className="overflow-hidden text-ellipsis font-mono !text-[12px]"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn(adminButton.outline, 'w-full shrink-0 sm:w-auto')}
                            onClick={() => {
                              navigator.clipboard.writeText(
                                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ical-export?memberId=${selectedMember.id}&token=${selectedMember.ical_token ?? ''}`
                              );
                              toast({ title: 'Copied', description: 'iCal URL copied to clipboard' });
                            }}
                          >
                            <Copy className="h-4 w-4" />
                            Copy
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </Panel>
        </>
      )}

      {/* Edit Member Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>Update team member details</DialogDescription>
          </DialogHeader>
          {editMember && (
            <div className="adm-form space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={editMember.name}
                  onChange={(e) => setEditMember({ ...editMember, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editMember.email}
                  onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input
                  value={editMember.phone || ''}
                  onChange={(e) => setEditMember({ ...editMember, phone: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select
                  value={editMember.role}
                  onValueChange={(value: TeamMemberRole) => setEditMember({ ...editMember, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="cleaner">Cleaner</SelectItem>
                  </SelectContent>
                </Select>
                <p className="adm-hint">
                  {editMember.role === 'admin' && 'Full access to all features and website editing'}
                  {editMember.role === 'supervisor' && 'Can view all calendars and manage enquiries, no website editing'}
                  {editMember.role === 'cleaner' && 'Can only view and manage their own calendar'}
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" className={adminButton.primary} onClick={handleUpdateMember} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default TeamAdmin;
